import { observer } from "mobx-react";
// student env specific navigation menu
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProduct } from "hooks/useProduct";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import profileIcon from "assets/images/icon-in-circle.svg";
import { NavigationMenu } from "components/NavigationMenuV2/NavigationMenu";
import type { NavigationMenuItem } from "components/NavigationMenuV2/NavigationMenu.model";
import { TestType } from "constants/exam-constants";
import {
  GENERIC_ROUTES,
  PRODUCT_PAGES_ROUTES,
  RESULT_PAGE_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEST_ENVIRONMENT_ROUTES,
  TEST_PAGE_ROUTES,
} from "constants/routes";

import { ROLES } from "@infinitaslearning/module-he-common";
import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import { AccessibilityMenuModal } from "components/AccessibilityMenuModal/AccessibilityMenuModal";
import {
  StyledAccessibilityMenuButton,
  StyledButton,
  StyledDropdownIcon,
  StyledIcon,
  StyledLogo,
  StyledLogoContainer,
  StyledNavigationBottomContainer,
  StyledNavigationContainer,
  StyledNavigationItemName,
  StyledOrphanNavigationItemName,
  StyledProfileButton,
} from "components/NavigationMenuV2/NavigationMenu.styled";
import { SideMenuModal } from "components/SideMenuModal";
import { ProductType, UsetifulTour } from "constants/hst-constants";
import { modalIds } from "constants/modals-constants";
import { useCurrentPathMatch } from "hooks/useCurrentPathMatch";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useModal } from "hooks/useModal";
import { useModuleAvailability } from "hooks/useModuleAvailability";
import { useUsetiful } from "hooks/useUsetiful";
import { RoleComponent } from "layouts/RoleComponent";
import type { User, UserUpdate } from "models/user/User";
import type { ROLE } from "persistence";
import { Link, useHistory, useLocation } from "react-router-dom";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { CssFlex } from "styles/helpers/layout";

interface ComponentProps {
  className?: string;
  dataCy?: string;
  userDetails?: User;
  sideMenuEnabled?: boolean;
  onLogout: () => void;
  toggleSideMenu: () => void;
  updateUserDetails: (userDetails: UserUpdate) => void;
}

export const StudentNavigationMenu: React.FC<ComponentProps> = observer(
  ({
    className,
    dataCy,
    sideMenuEnabled = true,
    userDetails,
    toggleSideMenu,
    updateUserDetails,
    onLogout,
  }) => {
    const { t } = useTranslation("homepage");
    const history = useHistory();
    const location = useLocation();

    const { productDetails: product, productsList: ownedProducts, fetchProducts } = useProduct();
    const isStudentABusinessUser =
      ownedProducts?.length &&
      ownedProducts?.every((product) => product.type === ProductType.BUSINESS);
    const {
      studentNavigationPersonalProgress,
      studentNavigationResultsWritingExercises,
      studentNavigationDiagnosticTest,
      showImmersiveReader,
      enableUxImprovements,
    } = useFeatureFlags();
    const { isBusinessDomain, getCustomLogoByDomain } = useDomainHandler();
    const { toggleModal, isModalOpen } = useModal();
    const { isUsetifulTourAPIAvailable, openTour } = useUsetiful();
    const { currentRouteMatchesAny } = useCurrentPathMatch();

    const getUrlWithProductId = useCallback(
      (placeholderUrl: string) =>
        buildUrlWithPathParams(placeholderUrl, { productId: product?.id as number }),
      [product],
    );

    useEffect(() => {
      fetchProducts();
    }, [fetchProducts]);

    const { isAdaptiveModuleAvailable, isWritingModuleAvailable } = useModuleAvailability();

    const isTestsExpanded =
      location.pathname.includes("/diagnostic-test") || location.pathname.startsWith("/test");
    const isResultsExpanded =
      location.pathname.includes("/results") || location.pathname.includes("/certificates");

    const newMenuItems = useMemo<NavigationMenuItem[]>(
      () => [
        {
          id: "student-dashboard",
          dataCy: "student-dashboard",
          nestingLevel: 0,
          title: (
            <StyledOrphanNavigationItemName>
              <StyledIcon iconName="far fa-home" iconSize="lg" />
              {t("sideMenu.dashboard", "Dashboard")}
            </StyledOrphanNavigationItemName>
          ),
          redirectPath:
            ownedProducts?.length === 0
              ? PRODUCT_PAGES_ROUTES.NO_PRODUCT_LICENSE
              : getUrlWithProductId(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS),
          onlyActiveOnExactPath: true,
        },
        /* Show module as standalone item if product has only one; otherwise we wrap them into a category */
        ...(product?.modules && product.modules.length === 1
          ? [
              {
                id: `product-module-${product.modules[0].id}`,
                title: (
                  <StyledNavigationItemName>
                    <StyledIcon iconName="far fa-dot-circle" iconSize="lg" />
                    {product.modules[0].title}
                  </StyledNavigationItemName>
                ),
                dataCy: "product-module-item",
                redirectPath: buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
                  productId: product.id,
                  moduleId: product.modules[0].id,
                }),
                nestingLevel: 0,
                excludeFromActivePath: [
                  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
                  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
                  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
                ],
              },
            ]
          : [
              {
                id: "product-modules",
                dataCy: "product-module-list",
                expandedByDefault: true,
                title: (
                  <StyledNavigationItemName>
                    <StyledIcon iconName="far fa-dot-circle" />
                    {t("sideMenu.modules.title", "Modules")}
                    <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
                  </StyledNavigationItemName>
                ),
                subItems: product?.modules.flatMap((module) => {
                  const defaultModuleMenuItem = {
                    id: `product-module-${module.id}`,
                    title: module.title,
                    nestingLevel: 1,
                    dataCy: "module-list-item",
                    redirectPath: buildUrlWithPathParams(
                      PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS,
                      {
                        productId: product.id,
                        moduleId: module.id,
                      },
                    ),
                    excludeFromActivePath: [
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
                    ],
                  };
                  return [defaultModuleMenuItem];
                }),
              },
            ]),
        {
          id: "test-list",
          dataCy: "test-list",
          expandedByDefault: isTestsExpanded,
          title: (
            <StyledNavigationItemName>
              <StyledIcon iconName="far fa-flag" iconSize="lg" />
              {t("sideMenu.test.title", "Tests")}
              <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
            </StyledNavigationItemName>
          ),
          subItems: [
            ...(product?.modules[0]?.hasDiagnosticTest
              ? [
                  {
                    id: `product-module-${product?.modules[0].id}-diagnostic-test`,
                    title: t("sideMenu.modules.diagnosticTest"),
                    dataCy: `module-${product?.modules[0].id}-diagnostic-test`,
                    redirectPath: buildUrlWithPathParams(TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS, {
                      productId: product.id,
                      moduleId: product?.modules[0].id,
                    }),
                    nestingLevel: 1,
                    eventTrackData: {
                      module: STUDENT_MODULES.BASIC_PROGRESS,
                      itemId:
                        PLATFORM_EVENT_TYPES.STUDENT_MODULES_EVENT_TYPES.CLICK_GO_TO_PRACTICE_TEST,
                      elementId: `student-nav-menu-module-${product?.modules[0].id}-diagnostic-test`,
                    },
                  },
                ]
              : []),
            {
              nestingLevel: 1,
              id: "go-test-item",
              title: t("sideMenu.test.items.goToTestEnv", "Go to test"),
              dataCy: "go-test-item",
              redirectPath: TEST_ENVIRONMENT_ROUTES.LANDING_PAGE,
              excludeFromActivePath: [
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
              ],
              eventTrackData: {
                module: STUDENT_MODULES.DASHBOARD,
                itemId:
                  PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_GO_TEST_ENVIRONMENT,
                elementId: "student-nav-menu-go-to-test-env",
              },
            },
          ],
        },
        {
          id: "result-list",
          dataCy: "result-list",
          expandedByDefault: isResultsExpanded,
          title: (
            <StyledNavigationItemName>
              <StyledIcon iconName="fas fa-chart-line" iconSize="lg" />
              {t("sideMenu.results.title", "Results")}
              <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
            </StyledNavigationItemName>
          ),
          subItems: [
            ...(ownedProducts && ownedProducts.length > 0
              ? [
                  ...(studentNavigationPersonalProgress && isAdaptiveModuleAvailable
                    ? [
                        {
                          id: "results-personal-progress",
                          title: t("sideMenu.results.items.personalProgress", "Basic skills"),
                          dataCy: "results-personal-progress",
                          redirectPath: getUrlWithProductId(RESULT_PAGE_ROUTES.PERSONAL_PROGRESS),
                          nestingLevel: 1,
                        },
                      ]
                    : []),
                  ...(studentNavigationResultsWritingExercises && isWritingModuleAvailable
                    ? [
                        {
                          id: "results-writing-exercises",
                          title: t("sideMenu.results.items.writingExercises", "Writing skills"),
                          dataCy: "results-writing-exercises",
                          nestingLevel: 1,
                          redirectPath: getUrlWithProductId(RESULT_PAGE_ROUTES.WRITING_EXERCISES),
                        },
                      ]
                    : []),
                  ...(studentNavigationDiagnosticTest && !isStudentABusinessUser
                    ? [
                        {
                          id: "results-diagnostic-test",
                          dataCy: "results-diagnostic-test",
                          nestingLevel: 1,
                          title: t("sideMenu.results.items.diagnosticTest", "Practice Test"),
                          redirectPath: getUrlWithProductId(RESULT_PAGE_ROUTES.DIAGNOSTIC_TEST),
                        },
                      ]
                    : []),
                ]
              : []),
            ...(!isStudentABusinessUser
              ? [
                  {
                    id: "results-freebie-test",
                    dataCy: "results-freebie-test",
                    nestingLevel: 1,
                    title: t("sideMenu.results.items.freebieTest", "Freebie Test"),
                    redirectPath: buildUrlWithPathParams(
                      RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST,
                      {
                        testType: TestType.INTAKE_TEST,
                      },
                    ),
                  },
                  {
                    id: "results-formative-test",
                    dataCy: "results-formative-test",
                    nestingLevel: 1,
                    title: t("sideMenu.results.items.formativeTest", "Formative Test"),
                    redirectPath: buildUrlWithPathParams(
                      RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST,
                      {
                        testType: TestType.FORMATIVE_TEST,
                      },
                    ),
                  },
                  {
                    id: "results-summative-test",
                    dataCy: "results-summative-test",
                    nestingLevel: 1,
                    title: t("sideMenu.results.items.summativeTest", "Summative Test"),
                    redirectPath: buildUrlWithPathParams(
                      RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST,
                      {
                        testType: TestType.SUMMATIVE_TEST,
                      },
                    ),
                  },
                  {
                    id: "certificate-results",
                    dataCy: "certificate-results",
                    nestingLevel: 1,
                    title: t("sideMenu.results.items.certificate", "Certificates"),
                    redirectPath: RESULT_PAGE_ROUTES.OBTAINED_CERTIFICATES,
                  },
                ]
              : []),
          ],
        },
      ],
      [
        t,
        ownedProducts,
        isStudentABusinessUser,
        product,
        getUrlWithProductId,
        isWritingModuleAvailable,
        isAdaptiveModuleAvailable,
        studentNavigationPersonalProgress,
        studentNavigationResultsWritingExercises,
        studentNavigationDiagnosticTest,
        isTestsExpanded,
        isResultsExpanded,
      ],
    );

    const handleOnboardingButtonClick = () => {
      trackPlatformEvent({
        module: STUDENT_MODULES.DASHBOARD,
        itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_BEGIN_ONBOARDING,
        value: JSON.stringify({
          isBusinessDomain: isBusinessDomain(),
        }),
      });
      openTour(
        isBusinessDomain()
          ? UsetifulTour.BUSINESS_STUDENT_ONBOARDING
          : UsetifulTour.REGULAR_STUDENT_ONBOARDING,
      );
    };

    // Onboarding button should only show in some specific routes
    const showOnboardingButton =
      isUsetifulTourAPIAvailable() &&
      currentRouteMatchesAny([
        PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS,
        PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS,
      ]);

    const onMenuButtonClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      toggleSideMenu();
    };

    const onCloseSideMenu = () => {
      if (isModalOpen(modalIds.modalSideMenu)) {
        toggleModal(modalIds.modalSideMenu);
      }
    };

    return (
      <aside>
        <StyledNavigationContainer
          gridTemplateColumns="1fr"
          gridTemplateRows={!isBusinessDomain() ? "auto 1fr auto" : "1fr auto"}
        >
          {!isBusinessDomain() && (
            <StyledLogoContainer>
              <Link aria-label={t("header.button.goToMainPage")} to={GENERIC_ROUTES.HOME_PAGE}>
                <StyledLogo dataCy="sidebar-app-logo" iconName={getCustomLogoByDomain()} />
              </Link>
            </StyledLogoContainer>
          )}
          <NavigationMenu className={className} dataCy={dataCy} menuItems={newMenuItems} />
          <StyledNavigationBottomContainer>
            {showImmersiveReader && <StyledAccessibilityMenuButton />}
            {showOnboardingButton && (
              <StyledButton
                dataCy="open-onboarding-tour-button"
                variant="default"
                onClick={handleOnboardingButtonClick}
              >
                <StyledIcon iconName="far fa-question-circle" iconSize="lg" />
                {t("header.openOnboarding")}
              </StyledButton>
            )}
            {enableUxImprovements && (
              <RoleComponent allowedRoles={[ROLES.TEACHER as ROLE]}>
                <StyledButton
                  dataCy="teacher-header-go-to-student-env-button"
                  variant="default"
                  onClick={() => history.push(TEACHER_HOME_PAGE_ROUTES.HOME_PAGE)}
                >
                  <StyledIcon iconName="fas fa-chalkboard-teacher" iconSize="lg" />
                  {t("header.toTeacherEnvButton.title", "To teacher environment")}
                </StyledButton>
              </RoleComponent>
            )}
            {sideMenuEnabled && (
              <StyledProfileButton
                alignItems="center"
                data-cy="open-side-menu-button"
                gap={1}
                onClick={onMenuButtonClick}
              >
                <img alt="icon" src={profileIcon} />
                <CssFlex flexDirection="column">
                  <ParagraphLarge data-cy="account-header-name">{`${userDetails?.firstName} ${userDetails?.lastName}`}</ParagraphLarge>
                  <ParagraphMedium>{userDetails?.schoolNumber}</ParagraphMedium>
                </CssFlex>
              </StyledProfileButton>
            )}
          </StyledNavigationBottomContainer>
        </StyledNavigationContainer>
        {/* -- Modals (must be out of fixed nav container to avoid z-index issues) -- */}
        {sideMenuEnabled && (
          <>
            <SideMenuModal
              userDetails={userDetails}
              onClose={onCloseSideMenu}
              onLogout={onLogout}
              onSubmit={updateUserDetails}
            />
            <AccessibilityMenuModal onClose={onCloseSideMenu} />
          </>
        )}
      </aside>
    );
  },
);
